import { Controller } from "@hotwired/stimulus"

// Disables the form's submit button until two conditions are both met:
//   1. Every input flagged with [data-submit-gate-target="required"] has a
//      non-blank value.
//   2. The conflict-check turbo-frame inside the gate's element reports
//      data-state="available". Other states (pending, past, conflict) keep
//      the button disabled.
//
// Place this controller on the <form> (alongside form-submit / conflict-check)
// and the existing submit button is found by `[type="submit"]` lookup — no
// additional target wiring required.
//
// Targets:
//   required — every form field whose presence is required to submit
export default class extends Controller {
  static targets = ["required"]

  connect() {
    this._evaluate = this._evaluate.bind(this)
    this.element.addEventListener("change", this._evaluate)
    this.element.addEventListener("input", this._evaluate)
    // turbo:frame-load fires on the frame after a fetch completes; that's
    // how we learn the new conflict-check state.
    this.element.addEventListener("turbo:frame-load", this._evaluate)
    this._evaluate()
  }

  disconnect() {
    this.element.removeEventListener("change", this._evaluate)
    this.element.removeEventListener("input", this._evaluate)
    this.element.removeEventListener("turbo:frame-load", this._evaluate)
  }

  _evaluate() {
    const button = this.element.querySelector("[type='submit']")
    if (!button) return

    const allFilled = this.requiredTargets.every((el) => {
      const v = (el.value || "").trim()
      return v !== ""
    })
    const passable = this._conflictState() === "available"
    const enabled = allFilled && passable

    button.disabled = !enabled
    button.classList.toggle("opacity-50", !enabled)
    button.classList.toggle("cursor-not-allowed", !enabled)
    button.classList.toggle("hover:bg-primary-700", enabled)
  }

  _conflictState() {
    // The state lives on a child div inside the conflict_check turbo-frame
    // because Turbo replaces a frame's children, not the frame itself.
    const node = this.element.querySelector("[data-state]")
    return node ? node.dataset.state : null
  }
}
